import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { toLocalIsoDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Zap, Loader2 } from "lucide-react";

const QUANTITY_LABEL = {
  per_person: "guests",
  per_hour: "hours",
  per_item: "items",
  flat_rate: "",
};

function formatUSD(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);
}

/**
 * Compact instant-book widget rendered on the vendor profile.
 * Lists only products with booking_mode="instant"; falls back to nothing if
 * the vendor has no instant-bookable offerings. Server independently
 * re-computes the price at both create AND checkout time.
 */
export default function InstantBookSection({ vendor, user, date, period, durationHours, requiresDuration = false, eventId, available = true, unavailableReason, onLoaded }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [menuItems, setMenuItems] = useState(null);
  const [busyId, setBusyId] = useState("");

  // Tell the parent how many instant offerings exist once both fetches settle.
  useEffect(() => {
    if (products !== null && menuItems !== null) onLoaded?.(products.length + menuItems.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, menuItems]);

  useEffect(() => {
    if (!vendor?.vendor_id) return;
    let cancelled = false;
    api.get(`/vendors/${vendor.vendor_id}/products`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setProducts(list.filter((p) => p?.booking_mode === "instant" && Number(p?.price) > 0));
      })
      .catch(() => { if (!cancelled) setProducts([]); });
    api.get(`/vendors/${vendor.vendor_id}/menus`)
      .then(({ data }) => {
        if (cancelled) return;
        const menus = Array.isArray(data) ? data : [];
        // Flatten menu → categories → items where instant-eligible.
        const items = [];
        for (const m of menus) for (const c of (m.categories || [])) for (const it of (c.items || [])) {
          if (it?.booking_mode === "instant" && Number(it?.price) > 0 && it?.available !== false) {
            items.push({ ...it, __source: "menu_item" });
          }
        }
        setMenuItems(items);
      })
      .catch(() => { if (!cancelled) setMenuItems([]); });
    return () => { cancelled = true; };
  }, [vendor?.vendor_id]);

  // Defensive role guard — planner booking flows are strictly planner-only.
  // Backend already rejects non-planner roles with 403; this hides the UI so
  // vendors browsing another vendor's profile never see actionable planner CTAs.
  // Placed after all hooks to comply with Rules of Hooks.
  if (user && user.role !== "planner") return null;

  const nothing = (products === null || products.length === 0) && (menuItems === null || menuItems.length === 0);
  if (nothing) return null;

  // Event context is required: the server derives the service date from the
  // selected event and re-validates ownership + availability.
  const guard = () => {
    if (!user) { navigate("/login"); return false; }
    if (!eventId) { toast.error("Select an event first"); return false; }
    if (!available) { toast.error(unavailableReason || "This vendor is unavailable for your event date."); return false; }
    if (requiresDuration && !durationHours) { toast.error("Please select how many hours you need"); return false; }
    return true;
  };
  const basePayload = () => ({
    vendor_id: vendor.vendor_id, event_id: eventId, period,
    date: date ? toLocalIsoDate(date) : "",
    ...(requiresDuration ? { duration_hours: durationHours } : {}),
  });

  const bookProduct = async (p, quantity) => {
    if (!guard()) return;
    setBusyId(p.product_id);
    try {
      const { data } = await api.post("/bookings/instant", {
        ...basePayload(), source_type: "product", product_id: p.product_id, quantity,
        guest_count: p.pricing_type === "per_person" ? quantity : 0,
      });
      toast.success("Booking created — complete payment to confirm.");
      navigate(`/bookings/${data.booking_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create booking"); }
    finally { setBusyId(""); }
  };

  const bookMenuItem = async (mi, quantity) => {
    if (!guard()) return;
    setBusyId(mi.item_id);
    try {
      const { data } = await api.post("/bookings/instant", {
        ...basePayload(), source_type: "menu_item", menu_item_id: mi.item_id, quantity,
        guest_count: mi.pricing_type === "per_person" ? quantity : 0,
      });
      toast.success("Booking created — complete payment to confirm.");
      navigate(`/bookings/${data.booking_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create booking"); }
    finally { setBusyId(""); }
  };

  const cardsDisabled = busyId !== "" || !user || !eventId || !available;

  return (
    <div data-testid="instant-book-section" className="mt-6 space-y-4">
      {products && products.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#F97316]" strokeWidth={2.5} /><div className="eyebrow">Packages &amp; services · Instant booking</div></div>
          {products.map((p) => (
            <InstantBookCard key={p.product_id} product={p} disabled={cardsDisabled} busy={busyId === p.product_id} onBook={({ quantity }) => bookProduct(p, quantity)} />
          ))}
        </div>
      )}
      {menuItems && menuItems.length > 0 && (
        <div className="space-y-3" data-testid="instant-menu-items">
          <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#F97316]" strokeWidth={2.5} /><div className="eyebrow">Menu items · Instant booking</div></div>
          {menuItems.map((mi) => (
            <InstantBookCard key={mi.item_id} product={{ product_id: mi.item_id, title: mi.name, description: mi.description, price: mi.price, pricing_type: mi.pricing_type }} disabled={cardsDisabled} busy={busyId === mi.item_id} onBook={({ quantity }) => bookMenuItem(mi, quantity)} />
          ))}
        </div>
      )}
    </div>
  );
}

function InstantBookCard({ product, disabled, busy, onBook }) {
  const isFlat = product.pricing_type === "flat_rate";
  const unitLabel = QUANTITY_LABEL[product.pricing_type] || "";
  const [qty, setQty] = useState(isFlat ? 1 : (product.pricing_type === "per_person" ? 50 : 1));
  const subtotal = isFlat ? Number(product.price) : Number(product.price) * qty;
  const unitPrefix = product.pricing_type === "per_person" ? "per person"
                    : product.pricing_type === "per_hour" ? "per hour"
                    : product.pricing_type === "per_item" ? "per item" : "flat rate";
  return (
    <div className="card-elegant p-4 flex flex-col gap-3" data-testid={`instant-card-${product.product_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px] leading-snug">{product.title}</div>
          <div className="text-[13px] text-[color:var(--text-muted)] mt-0.5">
            {formatUSD(product.price)} <span className="opacity-80">{unitPrefix}</span>
          </div>
          {product.description && (
            <div className="text-[12.5px] text-[color:var(--text-muted)] mt-1.5 line-clamp-2">{product.description}</div>
          )}
        </div>
      </div>
      {!isFlat && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setQty((q) => Math.max(1, q - (product.pricing_type === "per_person" ? 5 : 1)))}
            disabled={disabled}
            data-testid={`instant-qty-dec-${product.product_id}`}
            className="w-8 h-8 rounded-full border border-[color:var(--border)] flex items-center justify-center hover:bg-[color:var(--surface)] disabled:opacity-40"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <input
            type="number"
            min="1"
            value={qty}
            onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
            className="w-16 h-8 rounded-md border border-[color:var(--border)] text-center text-sm"
            data-testid={`instant-qty-input-${product.product_id}`}
          />
          <span className="text-xs text-[color:var(--text-muted)]">{unitLabel}</span>
          <button
            onClick={() => setQty((q) => q + (product.pricing_type === "per_person" ? 5 : 1))}
            disabled={disabled}
            data-testid={`instant-qty-inc-${product.product_id}`}
            className="w-8 h-8 rounded-full border border-[color:var(--border)] flex items-center justify-center hover:bg-[color:var(--surface)] disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Booking total</div>
            <div className="font-semibold text-[15px]" data-testid={`instant-total-${product.product_id}`}>{formatUSD(subtotal)}</div>
          </div>
        </div>
      )}
      {isFlat && (
        <div className="flex items-center gap-2">
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Total</div>
            <div className="font-semibold text-[15px]" data-testid={`instant-total-${product.product_id}`}>{formatUSD(subtotal)}</div>
          </div>
        </div>
      )}
      <Button
        onClick={() => onBook({ quantity: qty })}
        disabled={disabled}
        data-testid={`instant-book-btn-${product.product_id}`}
        className="btn-sage rounded-full h-10"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 mr-1.5" />} Book now
      </Button>
    </div>
  );
}
